import { getMediaFullUrl } from '@/functions/getMediaFullUrl';
import { useCarouselToggle } from '@/hooks/useCarouselToggle';
import { usePrefetchVideos } from '@/hooks/usePrefetchVideos';
import clsx from 'clsx';
import { Link } from 'gatsby';
import * as React from 'react';
import { CarouselHeader } from '../carouselHeader/CarouselHeader';
import { Container } from '../container/Container';
import { LocalVideoWithControls } from '../localVideoWithControls/LocalVideoWithControls';
import * as styles from './ItemCarousel.module.scss';

type CarouselItem = {
  title: string;
  description: string;
  categoryTitle: string;
  mediaUrl?: string;
  detailsLink: string;
  isVideo?: boolean;
};

type ItemCarouselProps = {
  items: CarouselItem[];
  linkText: string;
  className?: string;
  allItemsLink?: string;
};

export const ItemCarousel = ({ items, className, linkText, allItemsLink }: ItemCarouselProps) => {
  const { activeItemIndex, handlePrevClick, handleNextClick } = useCarouselToggle(items.length);
  const selectedItem = items[activeItemIndex];
  const colorsIndex = activeItemIndex % 5;

  const videoUrls = React.useMemo(
    () => items.filter((item) => item.isVideo && item.mediaUrl).map((item) => getMediaFullUrl(item.mediaUrl!)),
    [items],
  );
  usePrefetchVideos(videoUrls);

  const itemLink = allItemsLink || selectedItem.detailsLink;

  return (
    <div
      className={clsx(
        styles.itemCarousel,
        className,
        colorsIndex === 1 && styles.itemCarousel___one,
        colorsIndex === 2 && styles.itemCarousel___two,
        colorsIndex === 3 && styles.itemCarousel___three,
        colorsIndex === 4 && styles.itemCarousel___four,
      )}
    >
      <Container>
        <div className={styles.itemCarousel__inner}>
          <CarouselHeader className={styles.itemCarousel__header}>
            <CarouselHeader.Title>{selectedItem.categoryTitle}</CarouselHeader.Title>
            <div className={styles.itemCarousel__headerRight}>
              <CarouselHeader.Navigation onPrevClick={handlePrevClick} onNextClick={handleNextClick} />
              <Link to={itemLink} className={clsx(styles.itemCarousel__link, styles.itemCarousel__desktop)}>
                {linkText}
              </Link>
            </div>
          </CarouselHeader>
          <div className={styles.itemCarousel__content}>
            <div className={styles.itemCarousel__desc}>
              <p className={styles.itemCarousel__title}>{selectedItem.title}</p>
              <p className={styles.itemCarousel__text}>{selectedItem.description}</p>
            </div>
            <div className={styles.itemCarousel__linkVideoWrapper}>
              {selectedItem.mediaUrl &&
                (selectedItem.isVideo ? (
                  <LocalVideoWithControls src={getMediaFullUrl(selectedItem.mediaUrl)} />
                ) : (
                  <img
                    src={getMediaFullUrl(selectedItem.mediaUrl)}
                    alt={selectedItem.title}
                    className={styles.itemCarousel__image}
                  />
                ))}
              <Link to={itemLink} className={clsx(styles.itemCarousel__link, styles.itemCarousel__mobile)}>
                {linkText}
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};
